import { ViewerContext } from "@/Context/ViewerContext/ViewerContextProvider";
import Configurations from "@/components/Configurations/Configurations";
import ContextMenu from "@/components/ContextMenu/ContextMenu";
import IfcViewer from "@/components/IfcViewer/IfcViewer";
import Measurer from "@/components/Measurer/Measurer";
import Navcube from "@/components/Navcube/Navcube";
import ObjectViews from "@/components/ObjectViews/ObjectViews";
import ObjectViewsButton from "@/components/ObjectViews/ObjectViewsButton";
import OrbitPanToggle from "@/components/OrbitPanToggle/OrbitPanToggle";
import Picker from "@/components/Picker/Picker";
import PropertyWindow from "@/components/PropertyWindow/PropertyWindow";
import PropertyWindowButton from "@/components/PropertyWindow/PropertyWindowButton";
import SectionPlanes from "@/components/SectionPlanes/SectionPlanes";
import SnapshotMaker from "@/components/SnapshotMaker/SnapshotMaker";
import ZoomToFit from "@/components/ZoomToFit/ZoomToFit";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import useViewerStore from "@/stores/useViewerStore";
import { useContext, useRef } from "react";

const configuration = {
    id: "ifc-viewer",
    defaults: {
        IfcProject: {
            visible: false,
            priority: 0,
            pickable: false,
        },
        IfcSpace: {
            colorize: [1, 0.08, 0.58],
            opacity: 1,
            pickable: true,
        },
        IfcGrid: {
            colorize: [1, 0.08, 0.58],
            opacity: 0.2,
            pickable: false,
        },
    },
    materials: {
        highlight: {
            fill: true,
            fillColor: [0.94, 0.45, 0.23],
            fillAlpha: 0.3,
            edges: true,
            edgeColor: [0, 0, 0],
            edgeAlpha: 0.5,
            edgeWidth: 0.5,
        },
        xray: {
            fill: true,
            fillColor: [0, 0, 0],
            fillAlpha: 0.25,
            edges: true,
            edgeColor: [0, 0, 0],
            edgeAlpha: 0.2,
            edgeWidth: 1,
        },
        selected: {
            fill: true,
            fillColor: [0.21, 0.39, 0.48],
            fillAlpha: 0.5,
            edges: false,
            edgeColor: [0, 0, 0],
            edgeAlpha: 0,
            edgeWidth: 0,
        },
    },
};

const FireBimViewer = () => {
    const { state } = useContext(ViewerContext);
    const { modelBrowserOpen, propertiesPanelOpen } = useViewerStore();
    const isLongPressing = useRef(false);

    return (
        <main className="fixed w-full h-full">
            <ResizablePanelGroup direction="horizontal" autoSaveId="viewer-horizontal">
                <ResizablePanel id="main-panel" order={0} minSize={50} className="relative">
                    <div className="h-full">
                        <IfcViewer configuration={configuration} />
                        <ContextMenu isLongPressing={isLongPressing} canvasId={configuration.id} />
                        <Picker isLongPressing={isLongPressing} />
                        <Navcube useSpacer />

                        <div className="absolute bottom-[50px] left-1/2 transform -translate-x-1/2  flex">
                            <div className="flex p-1 border border-gray-200 dark:border-slate-600 bg-slate rounded-md bg-white dark:bg-slate-950">
                                <OrbitPanToggle />
                                <ZoomToFit />
                            </div>
                            <div className="flex ml-2 p-1 border border-gray-200 dark:border-slate-600 bg-slate rounded-md items-center bg-white dark:bg-slate-950">
                                {state.openPlugin != "SectionPlanes" && <Measurer />}
                                {state.openPlugin != "Measurer" && <SectionPlanes />}
                            </div>
                            <div className="flex ml-2 p-1 border border-gray-200 dark:border-slate-600 bg-slate rounded-md bg-white dark:bg-slate-950">
                                <ObjectViewsButton />
                                <PropertyWindowButton />
                                <SnapshotMaker />
                                <Configurations />
                            </div>
                        </div>
                    </div>
                </ResizablePanel>

                {(modelBrowserOpen || propertiesPanelOpen) && (
                    <>
                        <ResizableHandle withHandle />

                        <ResizablePanel id="side-panel" order={1} defaultSize={50} minSize={25} className="m-0 z-50">
                            <ResizablePanelGroup direction="vertical" autoSaveId="viewer-vertical">
                                {modelBrowserOpen && (
                                    <ResizablePanel id="object-views-panel" order={0} minSize={25}>
                                        <ObjectViews />
                                    </ResizablePanel>
                                )}

                                {modelBrowserOpen && propertiesPanelOpen && <ResizableHandle withHandle />}

                                {propertiesPanelOpen && (
                                    <ResizablePanel id="property-window-panel" order={1} defaultSize={50} minSize={25}>
                                        <PropertyWindow />
                                    </ResizablePanel>
                                )}
                            </ResizablePanelGroup>
                        </ResizablePanel>
                    </>
                )}
            </ResizablePanelGroup>
        </main>
    );
};

export default FireBimViewer;
